import { useState } from "react"
import CRUDList from "../../components/CRUDList"
import Modal from "../../components/Modal"
import OrderedListItem from "../../components/OrderedListItem"
import PayeeProductSelectionList from "../PayeeProductSelectionList"
import ProductAddonConfigListItem from "../../components/ProductAddonConfigListItem"

import {useSelector} from "react-redux"

import { moveArrayElement } from "../../utils/generalUtils"
import {v4 as uuid4} from "uuid"

/**
 * A picker to select and configure product addons in a list. Product addons can be reordered.
 * Selection occurs in a modal that displays a ProductSelectionList
 * 
 */
const DEFAULT_PRODUCT_ADDON_CONFIG = {
    id: "",
    productId: "",
    productName: "",
    groupLabel: "",
    useProductNameAsLabel: true,
    required: false,
    selectMultiple: false,
    selectMax: null,
    selectMin: null,
    addToPrice: true,
    allAddonsOnAllVariants: true,
    allowedAddonVariantsByProductVariantId: {}
}

const PayeeProductAddonPicker = ({
    title="Products",
    payeeId="",
    payeeProductAddonConfigArray = [], 
    onChange=()=>{}
}) => {
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const payeeProductsById = useSelector(state => state.payeeProducts.payeeProductsById || {})
    const payeeAddonConfigsById = {} //used to display the selected addon config in the CRUDlist
    const selectedPayeeProductsById = {} //used to display the selected products in the PayeeProductSelectionList 
    payeeProductAddonConfigArray.forEach(addonConfig => {
        //index the product addon configs
        payeeAddonConfigsById[addonConfig.id] = addonConfig
        //index the selected products
        const product = payeeProductsById[addonConfig.productId]
        if (product) {
            selectedPayeeProductsById[addonConfig.productId] = product
        }
    })
    //used to sort the payee product addon configs
    const indexMap = Object.fromEntries(payeeProductAddonConfigArray.map((addonConfig, index) => [addonConfig.id, index]));
    //called when items are added / removed via the PayeeProductSelectionList
    const handleChangeSelection = (selectedProductIdMap={}) => {
        //loop over the previously provided list, and preserve the order of these
        //list elements that remain
        let sortedList = []
        //newly added items
        const newlyAddedMap = {...selectedProductIdMap}
        let newlyAddedList = [] 
        //remove any addonConfigs associated with a now-removed product
        payeeProductAddonConfigArray.forEach(addonConfig => {
            const {productId=""} = addonConfig || {}
            //if it existed, add it, while preserving the established ordering
            if (selectedProductIdMap[productId]) {
                sortedList.push(addonConfig)
                //remove existing products, so we can detect the newly added ones
                delete newlyAddedMap[productId]
            } 
        })
        //loop through the new products and created addonConfigs for them
        Object.keys(newlyAddedMap).forEach(productId => {
            const product = payeeProductsById[productId]
            const {name:productName="", id=""} = product || {}
            //create addon config
            newlyAddedList.push({
                ...DEFAULT_PRODUCT_ADDON_CONFIG,
                productId: id,
                productName,
                id: uuid4()
            })
        })
        //add any remaining elements in the list to the end of the array
        sortedList = [...sortedList, ...newlyAddedList ]
        onChange(sortedList)
    }

    const handleEditAddonConfig = (editedAddonConfig, i) => {
        const editedList = [...payeeProductAddonConfigArray]
        editedList[i] = editedAddonConfig
        onChange(editedList)
    } 
    const handleClickUp = i => onChange(moveArrayElement(payeeProductAddonConfigArray, i, i-1))
    const handleClickDown = i => onChange(moveArrayElement(payeeProductAddonConfigArray, i, i+1))
    const handleDelete = id => onChange(payeeProductAddonConfigArray.filter(addonConfig => addonConfig.id !== id))
    const handleDuplicate = addonConfig => {
        const {id:addonConfigId=""} = addonConfig || {}
        const currentIndex = indexMap[addonConfigId]
        const editedList = [...payeeProductAddonConfigArray]
        const newAddonConfig = {
            ...addonConfig,
            id: uuid4()
        }
        editedList.splice(currentIndex, 0, newAddonConfig);
        onChange(editedList)
    }
    return (
        <div>
            <CRUDList 
                title={title}
                placeholder={<div className="display-flex justify-content-center padding-05em">No Add-ons selected</div>}
                createIcon="add"
                createLabel="Add"
                objectsById={payeeAddonConfigsById}
                showDefaultRadioButton={false}
                defaultObjectId={""}
                showEditMenuItem={false}
                showDeleteMenuItem={true}
                showActionMenu={true}
                actionMenuItems={
                    [{
                        label: "Duplicate add-on",
                        onClick: addonConfig => handleDuplicate(addonConfig)
                    }]
                }
                renderItem={
                    addonConfig => {
                        const {id:addonConfigId=""} = addonConfig || {}
                        const currentIndex = indexMap[addonConfigId]
                        return (
                            <OrderedListItem
                                onClickUp={() => handleClickUp(currentIndex) }
                                onClickDown={() => handleClickDown(currentIndex) }
                            >
                                <ProductAddonConfigListItem 
                                    addonConfig={addonConfig}
                                    onChange={editedAddonConfig => handleEditAddonConfig(editedAddonConfig, currentIndex)}
                                />
                            </OrderedListItem>
                        )
                    }      
                }
                renderItemContainerClassName="card margin-bottom-05em padding-5px"
                sortFunction={(oA, oB) => indexMap[oA.id] - indexMap[oB.id]}
                handleClickAddButton={() => setCreateModalOpen(true)}
                handleClickDeleteMenuItem={handleDelete}
            />
            {
                createModalOpen ?
                <Modal
                    isOpen={Boolean(createModalOpen)}
                    closeModal={() => setCreateModalOpen(false)}
                    showCloseButton={true}
                    className="height-fit-content max-height-100pct"
                    overlayClassName="flex-center padding-1pct"
                    innerClassName=""
                    closeOnOverlayClick={true}
                >
                    <div className="display-width-400px">
                    <PayeeProductSelectionList 
                        title="Select Add-ons"
                        placeholder={<div className="padding-1em background-color-light-grey text-align-center border-radius-4px">No add-ons available. <div className="font-size-12px text-align-left padding-1em">You have to tick "Yes, this product is an add-on" on every product you want to show up here</div></div>}
                        payeeId={payeeId}
                        filter={product => Boolean(product && product.isAddon)} //filter to only products that are addons
                        selectedIdMap={selectedPayeeProductsById}
                        onSubmit={selectedIdMap => {
                                handleChangeSelection(selectedIdMap)
                                setCreateModalOpen(false)
                            }
                        }
                    />
                    </div>
                </Modal>
                :
                null
            }
        </div>
    )
}

export default PayeeProductAddonPicker